import React from 'react';

interface Currency {
  code: string;
  name: string;
  symbol: string;
  is_active: boolean;
}

interface ExchangeRate {
  id: string;
  base_currency: string;
  quote_currency: string;
  rate: number;
}

interface CurrencyConverterProps {
  baseCurrency: string;
  targetCurrency: string;
  exchangeRates: ExchangeRate[];
}

export const useCurrencyConverter = ({ baseCurrency, targetCurrency, exchangeRates }: CurrencyConverterProps) => {
  const convertAmount = (amount: number): number => {
    if (baseCurrency === targetCurrency) return amount;
    
    // Direct conversion
    const directRate = exchangeRates.find(
      rate => rate.base_currency === baseCurrency && rate.quote_currency === targetCurrency
    );
    
    if (directRate) {
      return amount * directRate.rate;
    }
    
    // Reverse conversion
    const reverseRate = exchangeRates.find(
      rate => rate.base_currency === targetCurrency && rate.quote_currency === baseCurrency
    );
    
    if (reverseRate) {
      return amount / reverseRate.rate;
    }
    
    // Cross-currency conversion via USD (if available)
    if (baseCurrency !== 'USD' && targetCurrency !== 'USD') {
      const baseToUsd = exchangeRates.find(
        rate => rate.base_currency === baseCurrency && rate.quote_currency === 'USD'
      ) || exchangeRates.find(
        rate => rate.base_currency === 'USD' && rate.quote_currency === baseCurrency
      );
      
      const usdToTarget = exchangeRates.find(
        rate => rate.base_currency === 'USD' && rate.quote_currency === targetCurrency
      ) || exchangeRates.find(
        rate => rate.base_currency === targetCurrency && rate.quote_currency === 'USD'
      );
      
      if (baseToUsd && usdToTarget) {
        let usdAmount = amount;
        
        // Convert to USD
        if (baseToUsd.base_currency === baseCurrency) {
          usdAmount = amount * baseToUsd.rate;
        } else {
          usdAmount = amount / baseToUsd.rate;
        }
        
        // Convert from USD to target
        if (usdToTarget.base_currency === 'USD') {
          return usdAmount * usdToTarget.rate;
        } else {
          return usdAmount / usdToTarget.rate;
        }
      }
    }
    
    // If no conversion rate found, return original amount
    return amount;
  };

  const formatCurrency = (amount: number, currency: string): string => {
    const symbols: Record<string, string> = {
      'USD': '$',
      'THB': '฿',
      'LAK': '₭',
      'USDT': '₮'
    };

    const symbol = symbols[currency] || currency;
    const convertedAmount = convertAmount(amount);
    
    return `${symbol}${convertedAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  return { convertAmount, formatCurrency };
};